import React, { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import AxiosUtil from '../../Utils/AxiosUtil';
import {
    ManagedActionModal,
    ManagedActionModalState,
    ManagedApiError,
    buildManagedActionModalState,
} from '../Shared/ManagedHelpers';
import { invalidateReportMenu } from '../Shared/ReportMenuQuery';
import type { ColumnsType } from 'antd/es/table';
import {
    AddIcon,
    Alert as ServaAlert,
    Button,
    Checkbox,
    Col,
    Confirmation,
    DatabaseOutlinedIcon,
    DeleteIcon,
    EditIcon,
    Form,
    Input,
    Modal as ServaModal,
    Row,
    Space,
    Spin,
    Table,
    TeamOutlinedIcon,
    Text,
    Title,
    Tooltip,
    Card,
} from '@servahealth/serva-ui-kit';
import Administration from '../../Constants/Endpoints/Administration';
import { SimplifiedReport } from '../Shared/SimplifiedReport';
import EnableReportRequest from '../../Interfaces/Requests/Administration/EnableReportRequest';
import DisableReportRequest from '../../Interfaces/Requests/Administration/DisableReportRequest';
import GetReportAdminModelsRequest from '../../Interfaces/Requests/Administration/GetReportAdminModelsRequest';
import GetReportAdminModelsResponse from '../../Interfaces/Responses/Administration/GetReportAdminModelResponse';
import Report from './LNKReport';
import ReportUsers from './LNKReportUsers';
import ReportTenants from './LNKReportTenants';
import UndeleteIcon from '../Icons/UndeleteIcon';

type AdminReportProps = {};

const AdminReport: React.FC<AdminReportProps> = () => {
    const queryClient = useQueryClient();

    const [reports, setReports] = useState<SimplifiedReport[]>([]);
    const [filteredReports, setFilteredReports] = useState<SimplifiedReport[]>([]);
    const [upsertReportModalOpen, setUpsertReportModalOpen] = useState(false);
    const [manageUsersModalOpen, setManageUsersModalOpen] = useState(false);
    const [manageTenantsModalOpen, setManageTenantsModalOpen] = useState(false);
    const [currentSimplifiedReport, setCurrentSimplifiedReport] = useState<SimplifiedReport | null>();
    const [searchText, setSearchText] = useState('');
    const searchTextRef = useRef('');
    const [isShowDisabled, setIsShowDisabled] = useState(false);
    const [newReport, setNewReport] = useState(true);

    const [formAdminReport] = Form.useForm();

    const [currentError, setCurrentError] = useState('');

    const [manageActionModalState, setManageActionModalState] = useState<ManagedActionModalState>({
        open: false,
        mode: 'info',
        title: '',
        message: '',
        isError: false,
    });

    const showAdminReportInfoModal = (title: string, message: string, isError: boolean = true) => {
        setManageActionModalState(buildManagedActionModalState('info', title, message, isError));
    };

    const handleManageActionModalOk = () => {
        setManageActionModalState((currentState) => ({
            ...currentState,
            open: false,
        }));
    };

    const handleAdminReportFinishFailed = () => {
        showAdminReportInfoModal(
            'Manage Reports Error',
            'The Manage Reports form could not be submitted.  Review the form and try again.'
        );
    };

    const { isLoading: isGetReportsLoading, isError: isGetReportsError, error: getReportsError, refetch: refetchReports } = useQuery(
        ['admin-reports', isShowDisabled],
        async () => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}${Administration.GET_REPORT_ADMIN_MODELS}`;
                const requestData: GetReportAdminModelsRequest = {
                    gra_includeDisabled: isShowDisabled,
                };
                return await AxiosUtil.get<GetReportAdminModelsResponse[], GetReportAdminModelsRequest>(url, requestData);
            } catch (err) {
                setCurrentError('GET_REPORT_ADMIN_MODELS: [Get Admin Reports] Internal Error: ' + err);
                console.error('GET_REPORT_ADMIN_MODELS: [Get Admin Reports] Internal Error: ', err);
                return Promise.reject(err);
            }
        },
        {
            onError: (err: unknown) => {
                setCurrentError('GET_REPORT_ADMIN_MODELS: [Get Admin Reports] Internal Error: ' + err);
                console.error('GET_REPORT_ADMIN_MODELS: [Get Admin Reports] Internal Error: ', err);
            },
            onSuccess: (response: GetReportAdminModelsResponse[]) => {
                if (response) {
                    const currentSearchText = searchTextRef.current;

                    let simplifiedReports: SimplifiedReport[] = response.map(
                        (eachElement) => {
                            return {
                                reportId: eachElement.id,
                                reportName: eachElement.name,
                                reportGroupName: eachElement.groupName,
                                isDashboard: eachElement.isDashboard,
                                useDatasetInputs: eachElement.useDatasetInputs,
                                released: eachElement.released,
                                apiOnly: eachElement.apiOnly,
                                exportExcel: eachElement.exportExcel,
                                exportPDF: eachElement.exportPDF,
                                exportPassword: eachElement.exportPassword,
                                tenantId: eachElement.tenantID,
                                tenantName: eachElement.tenantName,
                                deleted: eachElement.deleted,
                            };
                        }
                    );

                    if (!isShowDisabled) {
                        simplifiedReports = simplifiedReports.filter(
                            (eachReport) => !eachReport.deleted
                        );
                    }

                    setReports(simplifiedReports);
                    setFilteredReports(filterReportsBySearchText(simplifiedReports, currentSearchText));
                }
            },
        }
    );

    const { mutate: disReport, isLoading: isDisReportLoading, isError: isDisReportError, error: getDisReportError } = useMutation(
        async (aReport: SimplifiedReport) => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}${Administration.DISABLE_REPORT_REQUEST}`;
                const requestData: DisableReportRequest = {
                    id: aReport.reportId,
                    name: aReport.reportName,
                    groupName: aReport.reportGroupName,
                };
                const responseData = await AxiosUtil.post<GetReportAdminModelsResponse, DisableReportRequest>(url, requestData);
                return responseData;
            } catch (err) {
                setCurrentError('DISABLE_REPORT_REQUEST: [Disable Report] Internal Error: ' + err);
                console.error('DISABLE_REPORT_REQUEST: [Disable Report] Internal Error: ', err);
                showAdminReportInfoModal(
                    'Disable Report Error',
                    `Report: ${aReport.reportName} Was Unable To Be Disabled.  FAILED: Contact Application Support`
                );
                return Promise.reject(err);
            }
        },
        {
            onError: (err: unknown, aReport: SimplifiedReport) => {
                setCurrentError('DISABLE_REPORT_REQUEST: [Disable Report] Internal Error: ' + err);
                console.error('DISABLE_REPORT_REQUEST: [Disable Report] Internal Error: ', err);
                showAdminReportInfoModal(
                    'Disable Report Error',
                    `Report: ${aReport.reportName} Was Unable To Be Disabled.  FAILED: Contact Application Support`
                );
            },
            onSuccess: async (response: GetReportAdminModelsResponse, aReport: SimplifiedReport) => {
                await refetchReports();
                invalidateReportMenu(queryClient);

                showAdminReportInfoModal(
                    'Disable Report',
                    `Report: ${aReport.reportName} Was Successfully Disabled.`,
                    false
                );
            },
        }
    );

    const { mutate: enaReport, isLoading: isEnaReportLoading, isError: isEnaReportError, error: getEnaReportError } = useMutation(
        async (aReport: SimplifiedReport) => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}${Administration.ENABLE_REPORT_REQUEST}`;
                const requestData: EnableReportRequest = {
                    id: aReport.reportId,
                    name: aReport.reportName,
                    groupName: aReport.reportGroupName,
                    isDashboard: aReport.isDashboard,
                    useDatasetInputs: aReport.useDatasetInputs,
                    released: aReport.released,
                    apiOnly: aReport.apiOnly,
                    exportExcel: aReport.exportExcel,
                    exportPDF: aReport.exportPDF,
                    exportPassword: aReport.exportPassword,
                };
                const responseData = await AxiosUtil.post<GetReportAdminModelsResponse, EnableReportRequest>(url, requestData);
                return responseData;
            } catch (err) {
                setCurrentError('ENABLE_REPORT_REQUEST: [Enable Report] Internal Error: ' + err);
                console.error('ENABLE_REPORT_REQUEST: [Enable Report] Internal Error: ', err);
                showAdminReportInfoModal(
                    'Restore Report Error',
                    `Report: ${aReport.reportName} Was Unable To Be Restored ([Re] Enabled.)  FAILED: Contact Application Support`
                );
                return Promise.reject(err);
            }
        },
        {
            onError: (err: unknown, aReport: SimplifiedReport) => {
                setCurrentError('ENABLE_REPORT_REQUEST: [Enable Report] Internal Error: ' + err);
                console.error('ENABLE_REPORT_REQUEST: [Enable Report] Internal Error: ', err);
                showAdminReportInfoModal(
                    'Restore Report Error',
                    `Report: ${aReport.reportName} Was Unable To Be Restored ([Re] Enabled.)  FAILED: Contact Application Support`
                );
            },
            onSuccess: async (response: GetReportAdminModelsResponse, aReport: SimplifiedReport) => {
                await refetchReports();
                invalidateReportMenu(queryClient);

                showAdminReportInfoModal(
                    'Restore Report',
                    `Report: ${aReport.reportName} Was Successfully Restored ([Re] Enabled.)`,
                    false
                );
            }
        }
    );

    const columns: ColumnsType<SimplifiedReport> = [
        { title: 'ID', dataIndex: 'reportId', key: 'reportId', width: 60, ellipsis: true,
            onCell: (record: SimplifiedReport) => ({
                style: { color: !record.deleted ? 'black' : 'red' },
            }),
            render: (text: number) => <div>{text}</div>,
        },
        { title: 'Name', dataIndex: 'reportName', key: 'reportName', width: 190, ellipsis: true },
        { title: 'Group Name', dataIndex: 'reportGroupName', key: 'reportGroupName', width: 190, ellipsis: true },
        { title: 'Dashboard ?', dataIndex: 'isDashboard', key: 'isDashboard', width: 120, align: 'center', render: (text: boolean) => (text ? <div>&#10004;</div> : <div></div>) },
        { title: 'Dataset Inputs ?', dataIndex: 'useDatasetInputs', key: 'useDatasetInputs', width: 120, align: 'center', render: (text: boolean) => (text ? <div>&#10004;</div> : <div></div>) },
        { title: 'Released ?', dataIndex: 'released', key: 'released', width: 120, align: 'center', render: (text: boolean) => (text ? <div>&#10004;</div> : <div></div>) },
        { title: 'API Only ?', dataIndex: 'apiOnly', key: 'apiOnly', width: 120, align: 'center', render: (text: boolean) => (text ? <div>&#10004;</div> : <div></div>) },
        { title: 'Export Excel ?', dataIndex: 'exportExcel', key: 'exportExcel', width: 120, align: 'center', render: (text: boolean) => (text ? <div>&#10004;</div> : <div></div>) },
        { title: 'Export PDF ?', dataIndex: 'exportPDF', key: 'exportPDF', width: 120, align: 'center', render: (text: boolean) => (text ? <div>&#10004;</div> : <div></div>) },
        { title: 'Export Password', dataIndex: 'exportPassword', key: 'exportPassword', width: 130, ellipsis: true },
        { title: 'Actions', key: 'action', width: 130, fixed: 'right',
            render: (text: unknown, record: SimplifiedReport) => (
                <Space size={0}>
                    <Tooltip title='Edit Report'>
                        <Button
                            size='small'
                            className='m-0 p-0'
                            type='link'
                            icon={<EditIcon />}
                            onClick={() => handleUpdateReport(record)}
                        />
                    </Tooltip>
                    <Tooltip title='Manage Users'>
                        <Button
                            size='small'
                            className='m-0 p-0'
                            type='link'
                            icon={<TeamOutlinedIcon />}
                            onClick={() => handleManageUsers(record)}
                        />
                    </Tooltip>
                    <Tooltip title='Manage Tenants'>
                        <Button
                            size='small'
                            className='m-0 p-0'
                            type='link'
                            icon={<DatabaseOutlinedIcon />}
                            onClick={() => handleManageTenants(record)}
                        />
                    </Tooltip>
                    {
                        (!record.deleted ?
                            <Tooltip title='Disable Report'>
                                <Confirmation
                                    title='Are you sure you want to disable this report?'
                                    onConfirm={() => disReport(record)}
                                    okText='Yes'
                                    cancelText='No'
                                >
                                    <Button 
                                        size='small'
                                        className='m-0 p-0'
                                        danger 
                                        type='link' 
                                        icon={<DeleteIcon />} 
                                    />
                                </Confirmation>
                            </Tooltip>
                            :
                            <Tooltip title='Restore ( [Re] Enable ) Report'>
                                <Confirmation
                                    title='Are you sure you want to restore ( [re] enable ) this report?'
                                    onConfirm={() => enaReport(record)}
                                    okText='Yes'
                                    cancelText='No'
                                >
                                    <Button
                                        size='small'
                                        className='m-0 p-0'
                                        type='link'
                                        icon={<UndeleteIcon />}
                                    />
                                </Confirmation>
                            </Tooltip>
                        )
                    }
                </Space>
            ),
        },
    ];

    const handleSearchTextChange = (aValue: React.ChangeEvent<HTMLInputElement>) => {
        const value = aValue.target.value;

        searchTextRef.current = value;
        setSearchText(value);
        setFilteredReports(filterReportsBySearchText(reports, value));
    };

    const handleIsShowDisabledChange = (aValue: { target: { checked: boolean } }) => {
        setIsShowDisabled(aValue.target.checked);
    };

    const handleUpdateReport = (aReport: SimplifiedReport) => {
        setNewReport(false);
        setCurrentSimplifiedReport(aReport);
        setUpsertReportModalOpen(true);
    };

    const handleManageUsers = (aReport: SimplifiedReport) => {
        setManageUsersModalOpen(true);
        setCurrentSimplifiedReport(aReport);
    };

    const handleManageTenants = (aReport: SimplifiedReport) => {
        setManageTenantsModalOpen(true);
        setCurrentSimplifiedReport(aReport);
    };

    const handleCreateReport = () => {
        setNewReport(true);
        setCurrentSimplifiedReport(null);
        setUpsertReportModalOpen(true);
    };

    const filterReportsBySearchText = (reportRows: SimplifiedReport[], value: string): SimplifiedReport[] => {
        const lowerCaseSearchText = value.trim().toLocaleLowerCase();

        if (lowerCaseSearchText === '') {
            return reportRows;
        }

        return reportRows.filter((eachReport) => {
            return (
                eachReport.reportName.toLowerCase().includes(lowerCaseSearchText) ||
                eachReport.reportGroupName.toLowerCase().includes(lowerCaseSearchText)
            );
        });
    };

    const doSearch = (sValue: string) => {
        searchTextRef.current = sValue;
        setSearchText(sValue);
        setFilteredReports(filterReportsBySearchText(reports, sValue));
    };

    const determineWhichError = () => {
        if ( isGetReportsError ) {
            return currentError + ' ' + ((getReportsError as ManagedApiError).response?.data?.message ?? '');
        }
        else if ( isDisReportError ) {
            return currentError + ' ' + ((getDisReportError as ManagedApiError).response?.data?.message ?? '');
        }
        else if ( isEnaReportError ) {
            return currentError + ' ' + ((getEnaReportError as ManagedApiError).response?.data?.message ?? '');
        }
        else {
            return '';
        }
    };

    return (
        <Card className='shadow-card admin-full-page-card' size='small'>
            <ManagedActionModal
                modalState={manageActionModalState}
                onOk={handleManageActionModalOk}
                onCancel={handleManageActionModalOk}
            />
            <ServaModal
                hideButtons={true}
                open={upsertReportModalOpen}
                onCancel={() => setUpsertReportModalOpen(false)}
            >
                <Report
                    id={currentSimplifiedReport?.reportId ?? 0}
                    name={currentSimplifiedReport?.reportName ?? ''}
                    groupName={currentSimplifiedReport?.reportGroupName ?? ''}
                    isDashboard={currentSimplifiedReport?.isDashboard ?? false}
                    useDatasetInputs={currentSimplifiedReport?.useDatasetInputs ?? false}
                    released={currentSimplifiedReport?.released ?? false}
                    apiOnly={currentSimplifiedReport?.apiOnly ?? false}
                    exportExcel={currentSimplifiedReport?.exportExcel ?? false}
                    exportPDF={currentSimplifiedReport?.exportPDF ?? false}
                    exportPassword={currentSimplifiedReport?.exportPassword ?? ''}
                    newReport={newReport}
                    close={() => setUpsertReportModalOpen(false)}
                    refetch={() => refetchReports()}
                ></Report>
            </ServaModal>
            <ServaModal
                destroyOnHidden={true}
                hideButtons={true}
                open={manageUsersModalOpen}
                width='65%'
                onCancel={() => setManageUsersModalOpen(false)}
            >
                <ReportUsers
                    reportId={currentSimplifiedReport?.reportId ?? 0}
                    reportName={currentSimplifiedReport?.reportName ?? ''}
                    reportGroupName={currentSimplifiedReport?.reportGroupName ?? ''}
                    close={() => setManageUsersModalOpen(false)}
                ></ReportUsers>
            </ServaModal>
            <ServaModal
                destroyOnHidden={true}
                hideButtons={true}
                open={manageTenantsModalOpen}
                width='65%'
                onCancel={() => setManageTenantsModalOpen(false)}
            >
                <ReportTenants
                    reportId={currentSimplifiedReport?.reportId ?? 0}
                    reportName={currentSimplifiedReport?.reportName ?? ''}
                    reportGroupName={currentSimplifiedReport?.reportGroupName ?? ''}
                    close={() => setManageTenantsModalOpen(false)}
                ></ReportTenants>
            </ServaModal>
            <Spin spinning={
                            isGetReportsLoading ||
                            isDisReportLoading ||
                            isEnaReportLoading
                           }>
                <div style={{ display: 'table', margin: 'auto', textAlign: 'center', width: '50%' }}>
                {
                    (
                        isGetReportsError ||
                        isDisReportError ||
                        isEnaReportError
                    )
                    &&
                    (
                        <ServaAlert
                            className={'mb-2'}
                            message={determineWhichError()}
                            showIcon={true}
                            type='error'
                        ></ServaAlert>
                    )
                }
                </div>
                <Space direction='horizontal' style={{justifyContent: 'center', padding: '8px', width: '100%'}}>
                    <Text style={{height: '100%', width: '100%'}}>
                        <Title style={{backgroundColor: 'rgb(00, 55, 90)', borderRadius: '10px', color: 'white', height: '100%', padding: '8px', width: '100%'}} level={4}>Manage Reports And Report Links To Tenants And Users</Title>
                    </Text>
                </Space>
                <Form
                    form={formAdminReport}
                    initialValues={{
                        ArIsShowDisabled: false,
                    }}
                    key='adminreport'
                    onFinishFailed={handleAdminReportFinishFailed}
                >
                    <Row gutter={16} style={{ padding: '16px' }}>
                        <Col span={2}>
                            <Button
                                type='primary'
                                style={{
                                    marginBottom: '15px',
                                    float: 'left',
                                }}
                                icon={<AddIcon />}
                                onClick={handleCreateReport}
                            >
                                Create Report
                            </Button>
                        </Col>
                        <Col span={2}></Col>
                        <Col span={8}>
                            <Input
                                allowClear={true}
                                search={true}
                                style={{width: '75%'}}
                                value={searchText}
                                onChange={handleSearchTextChange}
                                onSearch={doSearch}
                            ></Input>
                        </Col>
                        <Col span={3}></Col>
                        <Col span={3}>
                            <div
                                style={{
                                    height: '30px',
                                    paddingLeft: '5px'
                                }}
                            >
                                <Form.Item
                                    key='arisshowdisabled'
                                    name='ArIsShowDisabled'
                                    valuePropName='checked'
                                    style={{verticalAlign: 'middle'}}
                                >
                                    <Checkbox
                                        checked={isShowDisabled}
                                        style={{float: 'left', verticalAlign: 'middle'}}
                                        onChange={handleIsShowDisabledChange}
                                    >
                                        Show Disabled
                                    </Checkbox>
                                </Form.Item>
                            </div>
                        </Col>
                    </Row>
                    <Table
                        bordered
                        className='table-alternating-row-color'
                        columns={columns}
                        dataSource={filteredReports}
                        rowClassName={() => 'rowClassName1'}
                        rowKey='reportId'
                        size='small'
                        style={{ padding: '12px' }}
                    />
                </Form>
            </Spin>
        </Card>
    );
};

export default AdminReport;
